const FALLBACK_MEASUREMENT_ID = "G-SMPP0N8GN5";

export const GA_MEASUREMENT_ID =
  String(process.env.REACT_APP_GA_MEASUREMENT_ID || FALLBACK_MEASUREMENT_ID).trim();

export const ANALYTICS_CONSENT_KEY = "velsoft.analytics_consent.v1";

let bootstrapped = false;
let configured = false;

function canUseBrowser() {
  return typeof window !== "undefined" && typeof document !== "undefined";
}

function ensureDataLayer() {
  if (!canUseBrowser()) return;

  window.dataLayer = window.dataLayer || [];
  window.gtag =
    window.gtag ||
    function gtag() {
      window.dataLayer.push(arguments);
    };
}

function ensureGoogleAnalyticsLoaded() {
  if (!canUseBrowser() || !GA_MEASUREMENT_ID) return;

  ensureDataLayer();

  const scriptId = "velsoft-ga4-script";
  if (!document.getElementById(scriptId)) {
    const script = document.createElement("script");
    script.id = scriptId;
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(
      GA_MEASUREMENT_ID
    )}`;
    document.head.appendChild(script);
  }

  if (!configured) {
    configured = true;
    window.gtag("js", new Date());
    window.gtag("config", GA_MEASUREMENT_ID, {
      send_page_view: false,
      transport_type: "beacon",
    });
  }
}

export function getAnalyticsConsent() {
  if (!canUseBrowser()) return null;

  try {
    const value = window.localStorage.getItem(ANALYTICS_CONSENT_KEY);
    return value === "granted" || value === "denied" ? value : null;
  } catch (_) {
    return null;
  }
}

export function saveAnalyticsConsent(value) {
  if (!canUseBrowser()) return;

  try {
    window.localStorage.setItem(
      ANALYTICS_CONSENT_KEY,
      value === "granted" ? "granted" : "denied"
    );
  } catch (_) {
    // The current-page consent decision still works if localStorage is unavailable.
  }
}

export function bootstrapGoogleAnalytics() {
  if (!canUseBrowser() || bootstrapped || !GA_MEASUREMENT_ID) return;

  bootstrapped = true;
  ensureDataLayer();

  // Basic Google Consent Mode v2: default is denied. The Google script itself
  // is not requested until the visitor explicitly allows analytics.
  window.gtag("consent", "default", {
    analytics_storage: "denied",
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
    wait_for_update: 500,
  });

  if (getAnalyticsConsent() === "granted") {
    updateGoogleAnalyticsConsent(true);
  }
}

export function updateGoogleAnalyticsConsent(granted) {
  if (!canUseBrowser()) return;

  bootstrapGoogleAnalytics();
  ensureDataLayer();

  window.gtag("consent", "update", {
    analytics_storage: granted ? "granted" : "denied",
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
  });

  if (granted) {
    ensureGoogleAnalyticsLoaded();
  }
}

export function trackPageView(path) {
  if (!canUseBrowser() || getAnalyticsConsent() !== "granted") return;

  bootstrapGoogleAnalytics();
  ensureGoogleAnalyticsLoaded();

  const pagePath = path || `${window.location.pathname}${window.location.search}`;
  const pageLocation = `${window.location.origin}${pagePath}${window.location.hash || ""}`;

  // Avoid duplicate development-mode hits caused by React StrictMode.
  if (window.__velsoftLastGaPageView === pageLocation) return;
  window.__velsoftLastGaPageView = pageLocation;

  window.gtag("event", "page_view", {
    page_title: document.title,
    page_location: pageLocation,
    page_path: pagePath,
  });
}

export function trackEvent(eventName, params = {}) {
  if (
    !canUseBrowser() ||
    !eventName ||
    getAnalyticsConsent() !== "granted"
  ) {
    return;
  }

  bootstrapGoogleAnalytics();
  ensureGoogleAnalyticsLoaded();
  window.gtag("event", eventName, params);
}

export function trackWebVital(metric) {
  if (!metric || getAnalyticsConsent() !== "granted") return;

  trackEvent("web_vital", {
    metric_id: metric.id,
    metric_name: metric.name,
    metric_value: Math.round(
      metric.name === "CLS" ? metric.value * 1000 : metric.value
    ),
    metric_delta: Math.round(
      metric.name === "CLS" ? metric.delta * 1000 : metric.delta
    ),
    non_interaction: true,
  });
}
